import React, { useEffect, useState } from 'react';
import { useSocket } from '../../../shared/socket/SocketProvider';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import SidebarMyProfile from '../../sidebars/SidebarMyProfile';
import { useTranslation } from 'react-i18next';

type NotificationType = 'like' | 'chat' | 'follow' | 'moderation';
   
const IMAGE_CDN = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL || '';

const toImageUrl = (p?: string | null): string => {
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;
  return `${IMAGE_CDN.replace(/\/$/, '')}/${String(p).replace(/^\/+/, '')}`;
};
 
interface Notification {      
  _id: string;
  type: NotificationType;
  createdAt: string;
  read: boolean;
  userId: string;
  postId?: string;
  postTitle?: string;
  postCreatedAt?: string;
  fromUser?: {
    _id: string;
    profilename: string;
    picture?: string;
  };
  chatId?: string;
  content?: string;
  bundleKey?: string;
  mediaCount?: number;
}

const Notifications = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [liveNewIds, setLiveNewIds] = useState<Set<string>>(new Set()); // <- page-local highlights
  const { socket, connected } = useSocket();

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
const [deleteBusy, setDeleteBusy] = useState(false);

type FilterType = 'all' | 'chat' | 'like' | 'follow';

const [filter, setFilter] = useState<FilterType>('all');

const [initialLoading, setInitialLoading] = useState(true);

  const normalize = (x: any): Notification => ({
    ...x,
    _id: String(x._id),
    userId: String(x.userId),
    chatId: x.chatId ? String(x.chatId) : undefined,
    postId: x.postId ? String(x.postId) : undefined,
  });

  const timeStyle = (type: NotificationType): React.CSSProperties => ({
    color: '#777',
    display: 'inline-block',      // lets us nudge vertically
    ...(type === 'like' ? { position: 'relative', top: '-20%' } : {}),
  });

 
  const stripQuotes = (s?: string) => (s || '').replace(/^["'“”]+|["'“”]+$/g, '').trim();

  const LAST_SEEN_KEY = 'notifications.lastSeenAt';

  const lastSeenRef = React.useRef<number>(0);

  const openDM = (u?: { _id?: string; profilename?: string; picture?: string }) => {
  if (!u?._id) return;
  navigate('/chat', {
    state: {
      toUserId: u._id,
      toUserName: u.profilename,
      toUserPicture: toImageUrl(u.picture),
    },
  });
};


  // Last seen
useEffect(() => {
  try {
    const raw = sessionStorage.getItem(LAST_SEEN_KEY);
    lastSeenRef.current = raw ? parseInt(raw, 10) || 0 : 0;
  } catch {
    lastSeenRef.current = 0;
  }

  const mountedAt = Date.now();

  return () => {
    const elapsed = Date.now() - mountedAt;
    // Ignore StrictMode's immediate test unmount (<250ms)
    if (elapsed < 250) return;

    try { sessionStorage.setItem(LAST_SEEN_KEY, String(Date.now())); } catch {}
    // 🔔 tell the badge to clear NOW (only when actually leaving the page)
    window.dispatchEvent(new Event('notifications:resetBadge'));
  };
}, []);

  // Single socket effect: update list + mark page-local "new" ids
// Single socket effect: update list + mark page-local "new" ids
useEffect(() => {
  if (!socket || !connected) return;

  const onNew = (n: any) => {
    const nn = normalize(n);

    setNotifications(prev => {
      if (prev.some(p => p._id === nn._id)) return prev; // de-dupe
      return [nn, ...prev].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    });

    setLiveNewIds(prev => {
      const next = new Set(prev);
      next.add(String(nn._id));
      return next;
    });
  };

  const onDeleted = (id: string) => {
    const sid = String(id);
    setNotifications(prev => prev.filter(n => n._id !== sid));
    setLiveNewIds(prev => {
      if (!prev.has(sid)) return prev;
      const next = new Set(prev);
      next.delete(sid);
      return next;
    });
  };

  const onUpdated = (patch: Partial<Notification> & { _id: string }) => {
    setNotifications(prev =>
      prev.map(n => (n._id === String(patch._id) ? { ...n, ...patch } : n))
    );
  };

  const onReadAll = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  socket.on('notification:new', onNew);
  socket.on('notification:deleted', onDeleted);
  socket.on('notification:updated', onUpdated);
  socket.on('notification:read_all', onReadAll);

  return () => {
    socket.off('notification:new', onNew);
    socket.off('notification:deleted', onDeleted);
    socket.off('notification:updated', onUpdated);
    socket.off('notification:read_all', onReadAll);
  };
}, [socket, connected]);


  // Initial fetch (older items = no highlight)
useEffect(() => {
  const fetchInitial = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);

      const highlight = new Set<string>();
      for (const n of res.data as Notification[]) {
        const created = new Date(n.createdAt).getTime();
        if (created > lastSeenRef.current) highlight.add(String(n._id));
      }
      setLiveNewIds(highlight);

    } catch (err) {
      console.error('❌ Failed to fetch notifications:', err);
    } finally {
      setInitialLoading(false);
    }
  };

  if (token) fetchInitial();
}, [token]);

useEffect(() => {
  const styleId = 'spin-keyframes';

  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;

  style.innerHTML = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  document.head.appendChild(style);

  return () => {
    const existing = document.getElementById(styleId);
    if (existing) existing.remove();
  };
}, []);


  // Optional: your prefetch block (kept as-is)
  useEffect(() => {
    const TEN_MINUTES = 10 * 60 * 1000;
    const now = Date.now();
    const lastPrefetch = Number(localStorage.getItem('prefetchedMyLikesTime')) || 0;
    const isStale = now - lastPrefetch > TEN_MINUTES;
    const shouldPrefetch =
      isStale ||
      !localStorage.getItem('prefetchedMyLikes') ||
      !localStorage.getItem('prefetchedMyPosts');
    if (!shouldPrefetch) return;

    (async () => {
      try {
        const [likesRes, postsRes] = await Promise.all([
          api.get('/likes/liked/mine', { params: { limit: 3 } }),
          api.get('/my-posts', { params: { limit: 3 } }),
        ]);
        localStorage.setItem('prefetchedMyLikes', JSON.stringify(likesRes.data));
        localStorage.setItem('prefetchedMyPosts', JSON.stringify(postsRes.data));
        localStorage.setItem('prefetchedMyLikesTime', now.toString());
      } catch (err) {
        console.warn('⚠️ Prefetch failed (Notifications):', err);
      }
    })();
  }, [token]);

  const handleDeleteNotification = (id: string) => {
  setDeleteTargetId(id);
};

const confirmDelete = async () => {
  if (!deleteTargetId) return;

  try {
    setDeleteBusy(true);

    await api.delete(`/notifications/${deleteTargetId}`);

    setNotifications(prev =>
      prev.filter(n => n._id !== deleteTargetId)
    );

    setLiveNewIds(prev => {
      const next = new Set(prev);
      next.delete(deleteTargetId);
      return next;
    });

  } catch (err) {
    console.error('❌ Failed to delete notification:', err);
  } finally {
    setDeleteBusy(false);
    setDeleteTargetId(null);
  }
};

const filteredNotifications = notifications.filter(n => {
  if (filter === 'all') return true;
  return n.type === filter;
});


  return (
    <div style={{ display: 'flex', background: '#000', minHeight: '100vh' }}>
      <SidebarMyProfile/>
      <div
        style={{
          flexGrow: 1,
          padding: '8px 60px 60px 40px',
          marginLeft: '17%',
          marginTop: '2%',
          overflowY: 'auto',
          color: '#bbb',
        }}
      >

        {/* FILTER BAR */}
<div style={filterBarStyle}>
  <button
    onClick={() => setFilter('all')}
    style={filter === 'all' ? activeFilterBtn : filterBtn}
  >
    {t('notifications.filters.all', 'All')}
  </button>

  <button
    onClick={() => setFilter('chat')}
    style={filter === 'chat' ? activeFilterBtn : filterBtn}
  >
    💬 {t('notifications.filters.messages', 'Messages')}
  </button>

  <button
    onClick={() => setFilter('like')}
    style={filter === 'like' ? activeFilterBtn : filterBtn}
  >
    🖼 {t('notifications.filters.likes', 'Likes')}
  </button>

  <button
    onClick={() => setFilter('follow')}
    style={filter === 'follow' ? activeFilterBtn : filterBtn}
  >
    👤 {t('notifications.filters.follow', 'Follow')}
  </button>
</div>

{initialLoading ? (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '60vh',
      color: '#9ca3af',
      gap: 14,
    }}
  >
    <div
      style={{
        width: 28,
        height: 28,
        border: '3px solid rgba(255,255,255,0.15)',
        borderTop: '3px solid #cbd5f5',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }}
    />

    <div style={{ fontSize: 18 }}>
      {t('notifications.loading') || 'Loading notifications…'}
    </div>
  </div>

) : filteredNotifications.length === 0 ? (
  <p style={{ fontSize: '1.2rem', opacity: 0.8 }}>
  {filter === 'all' && t('notifications.empty.all')}
  {filter === 'chat' && t('notifications.empty.chat')}
  {filter === 'like' && t('notifications.empty.like')}
  {filter === 'follow' && t('notifications.empty.follow')}
</p>
) : (

          filteredNotifications.map((noti) => {

            const isLiveNew = liveNewIds.has(noti._id);
            const profile = noti.fromUser;
            const profilePic = profile?.picture ? toImageUrl(profile.picture) : null;

            return (
              <div
                key={noti._id}
                style={{
                  ...cardStyle,
                  border: '4px solid',
                  borderColor: isLiveNew ? '#d1ac52' : 'transparent', // <-- red border for arrivals while open
                }}        
              >
                {/* ❌ Delete Button */}
                <button
onClick={(e) => {
  e.stopPropagation();
  handleDeleteNotification(noti._id);
}}
                  title={t('notifications.delete.tooltip')}
                  style={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    background: 'transparent',
                    border: 'none',
                    fontSize: '18px',
                    color: '#000',
                    cursor: 'pointer',
                  }}
                >
                  ✖️
                </button>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {/* Profile Picture */}
                  <div
                    onClick={() =>
                      profile?._id === user?._id
                        ? navigate('/profile')
                        : navigate(`/visitor-profile/${profile?._id}`)
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === 'Enter' &&
                      (profile?._id === user?._id
                        ? navigate('/profile')
                        : navigate(`/visitor-profile/${profile?._id}`))
                    }
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: '5%',
                      backgroundColor: profilePic ? 'transparent' : 'rgb(38, 29, 162)',
                      backgroundImage: profilePic ? `url(${profilePic})` : 'none',
                      backgroundSize: 'cover',           
                      backgroundPosition: 'center',
                      marginRight: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: profilePic ? 0 : 26,
                      color: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    {!profilePic && '👻'}
                  </div>

                  {/* Notification Content */}
                  <div style={{ color: '#000' }}>

                  {noti.type === 'like' && (
  <p>
    <strong
      onClick={() => navigate(`/visitor-profile/${noti.fromUser?._id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) =>
        e.key === 'Enter' &&
        navigate(`/visitor-profile/${noti.fromUser?._id}`)
      }
      style={{ cursor: 'pointer', color: '#000' }}
    >
      🖼 {noti.fromUser?.profilename || t('notifications.common.someone')}
    </strong>{' '}
    {t('notifications.like.liked')}{' '}
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        if (noti.postId) {
          navigate('/my-posts', {
            state: { from: 'notifications', focusPostId: noti.postId },
          });
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && noti.postId) {
          e.stopPropagation();
          navigate('/my-posts', {
            state: { from: 'notifications', focusPostId: noti.postId },
          });
        }
      }}
      style={{
        textDecoration: 'underline',
        color: '#004766',
        fontWeight: 'bold',
        cursor: 'pointer',
      }}
    >
      {t('notifications.like.yourPost')}
    </span>
    {noti.postCreatedAt ? (
      <>
        {' '}
        {t('notifications.like.fromDate', {
          date: new Date(noti.postCreatedAt).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
        })}
      </>
    ) : (
      <> {t('notifications.like.unknownDate')}</>
    )}
  </p>
)}



{noti.type === 'follow' && (
  <p>
    <strong
      onClick={() => navigate(`/visitor-profile/${noti.fromUser?._id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) =>
        e.key === 'Enter' &&
        navigate(`/visitor-profile/${noti.fromUser?._id}`)
      }
      style={{ cursor: 'pointer', color: '#000' }}
    >
      👤 {noti.fromUser?.profilename || t('notifications.common.someone')}
    </strong>{' '}
    {t('notifications.follow.started')}
  </p>
)}



{noti.type === 'chat' && (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#000' }}>
    <span>💬</span>
    <strong>{noti.fromUser?.profilename || t('notifications.common.someone')}</strong>
    <span>{t('notifications.chat.sentYouA')}</span>
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); openDM(noti.fromUser); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.stopPropagation(); openDM(noti.fromUser); }
      }}
      style={{
        flex: 1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: '#004766',
        cursor: 'pointer',
        textDecoration: 'underline',
        fontWeight: 'bold',
      }}
      title={t('notifications.chat.openChat')}
    >
      {stripQuotes(noti.content) || t('notifications.chat.newMessage')}
    </span>
  </div>
)}


{noti.type === 'moderation' && (
  <p>
    <strong style={{ color: '#000' }}>⚠️ {t('notifications.moderationmessage.title')} </strong>{' '}
    <span style={{ color: '#000' }}>
      {noti.content || t('notifications.moderationmessage.newMessage')} 
    </span>
  </p>
)}


<small style={timeStyle(noti.type)}>
  {new Date(noti.createdAt).toLocaleString()}
</small>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {deleteTargetId && (
  <div style={confirmOverlay}>
    <div style={confirmCard} onClick={(e) => e.stopPropagation()}>
      <div style={{ color: "#000", marginBottom: 12, fontSize: 15 }}>
        {t("notifications.delete.confirm")}
      </div>

      <button
        onClick={confirmDelete}
        disabled={deleteBusy}
        style={primaryBtn}
      >
        {deleteBusy
          ? t("notifications.delete.deleting")
          : t("common.confirm")}
      </button>

      <button
        onClick={() => setDeleteTargetId(null)}
        disabled={deleteBusy}
        style={cancelBtn}
      >
        {t("common.cancel")}
      </button>
    </div>
  </div>
)}

    </div>
  );
};

// Uniform row height for all notifications
const ITEM_HEIGHT = 64;

const cardStyle: React.CSSProperties = {
  background: '#d9d4d4',
  color: '#000',
  height: ITEM_HEIGHT,             // <- fixed height
  padding: '0 16px',               // compact horizontal padding
  borderRadius: '10px',
  marginBottom: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  border: '1px solid transparent',  // red border will overwrite when “live new”
};

const confirmOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999999,
  touchAction: "none",
};

const confirmCard: React.CSSProperties = {
  width: "92%",
  maxWidth: 420,
  background: "#f3f3f3",
  borderRadius: 14,
  padding: 20,
  textAlign: "center",
  border: "1px solid #aaa",
};

const primaryBtn: React.CSSProperties = {
  width: "100%",
  padding: 14,
  borderRadius: 10,
  border: "none",
  background: "#b91c1c",
  color: "#fff",
  fontWeight: 700,
  marginBottom: 10,
  cursor: "pointer",
};

const cancelBtn: React.CSSProperties = {
  width: "100%",
  padding: 12,
  borderRadius: 10,
  border: "none",
  background: "#ddd",
  color: "#333",
  cursor: "pointer",
};

const filterBarStyle: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  marginBottom: 18,
  paddingBottom: 8,
  borderBottom: '1px solid #333',
};

const filterBtn: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 20,
  border: '1px solid #555',
  background: 'transparent',
  color: '#bbb',
  cursor: 'pointer',
  fontWeight: 600,
};

const activeFilterBtn: React.CSSProperties = {
  ...filterBtn,
  background: '#d1ac52',
  color: '#000',
  border: '1px solid #d1ac52',
};


export default Notifications;
